import {
  Stack,
  DetailsList,
  Spinner,
  Text,
  Breadcrumb,
  Separator,
  IBreadcrumbItem,
  IconButton,
  TooltipHost,
  Dialog,
  DialogType,
  DialogFooter,
  DefaultButton,
  PrimaryButton,
  TextField,
  Checkbox,
} from "@fluentui/react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ISite } from "../components/Customer/CustomerContentEditPanel";
import PageTitle from "../components/layout/pageTitle";
import useApi from "../hooks/useApi";
import useStateIfMounted from "../hooks/useStateIfMounted";
import { ICustomer } from "../models/ICustomer";
import { IReportTemplate } from "../models/IReportTemplate";
import { globalMessageState } from "../atoms/GlobalMessage";
import { useRecoilState } from "recoil";
import { PanelActionType, PanelActionOwner, FormMode } from "../enums";
import { getSelectItems } from "../components/General/RevizioField";
import { IReport } from "../models/IReport.ts";
import { usePersistedState } from "../hooks/usePersistedState";
import { useId } from "@fluentui/react-hooks";
import { DataTable } from "../components/General/DataTable";
import customers from "./customers";
import { ReportEditPanel } from "../components/Reports/ReportEditPanel";
import dayjs from "dayjs";
import { useTenantInfo } from "../hooks/useTenantInfo";

const Reports = () => {
  const tenantInfo = useTenantInfo();

  const { id, siteid } = useParams();

  const navigate = useNavigate();

  const [reports, setReports] = useState<IReport[]>([]);
  const [loading, setLoading] = useStateIfMounted(true);

  const [site, setSite] = useState<ISite>();
  const [customer, setCustomer] = useState<ICustomer>();

  const [, setGlobalMessage] = useRecoilState(globalMessageState);

  // Paging
  const [pageSize, setPageSize] = usePersistedState("CustomerPageSize", 10);
  const [currentPage, setCurrentPage] = usePersistedState(
    "CustomerCurrentPage",
    1,
  );

  // Searching
  const [searchTerm, setSearchTerm] = useState("");

  const [totalReports, setTotalReports] = useStateIfMounted<number>(0);

  const [currentReport, setCurrentReport] = useState<IReport>();

  const [isEditReportOpen, setIsEditReportOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [duplicateSourceReport, setDuplicateSourceReport] = useState<IReport>();
  const [duplicateReportName, setDuplicateReportName] = useState("");
  const [isDuplicatingReport, setIsDuplicatingReport] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteSourceReport, setDeleteSourceReport] = useState<IReport>();
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [isDeletingReport, setIsDeletingReport] = useState(false);

  const detailTooltipId = useId("detailTooltip");

  const api = useApi();

  const getCustomer = async () => {
    // Use the Customer ID from properties
    try {
      const result = await api.get(`/customers/${id}`);

      setCustomer(result.data);
    } catch (e) {
      console.error(e);

      setGlobalMessage({
        action: PanelActionType.Error,
        owner: PanelActionOwner.Customer,
        message: `Error Getting Customer ${id}`,
      });
    }
  };

  const getSite = async () => {
    // Use the Customer ID from properties
    try {
      const result = await api.get(`/sites/${siteid}`);

      setSite(result.data);
    } catch (e) {
      console.error(e);

      setGlobalMessage({
        action: PanelActionType.Error,
        owner: PanelActionOwner.Customer,
        message: `Error Getting Site ${siteid}`,
      });
    }
  };

  const getReports = async () => {
    let url = `/reports?select=name,report_id,status,created_at,customer_id,site_id,from_template_id&expand=customer,site,template&page=${currentPage}&limit=${pageSize}&search=${searchTerm}&status=Submitted`;

    if (id) {
      url = `/reports?customerId=${id}&select=name,report_id,status,created_at,customer_id,site_id,from_template_id&expand=customer,site,template&page=${currentPage}&limit=${pageSize}&search=${searchTerm}`;
    }

    if (siteid) {
      url = `/reports?siteId=${siteid}&select=name,report_id,status,created_at,customer_id,site_id,from_template_id&expand=customer,site,template&page=${currentPage}&limit=${pageSize}&search=${searchTerm}`;
    }

    //console.log('url', url)

    try {
      const result = await api.get(url);

      setReports(result.data.data);
      setTotalReports(result.data.count);

      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (id) {
      // getCustomer
      getCustomer();
    }

    if (siteid) {
      // getSite
      getSite();
    }
    getReports();
  }, [currentPage, searchTerm, pageSize]);

  function getBreadcrumbItems() {
    let items: IBreadcrumbItem[] = [];

    items.push({
      text: "Customers",
      key: "customers",
      onClick: () => navigate("/customers"),
    });

    customer &&
      items.push({
        text: customer.name,
        key: "customer",
        onClick: () => navigate(`/customer/${id}`),
      });

    site &&
      items.push({
        text: site.name,
        key: "site",
      });

    return items;
  }

  const openDuplicateDialog = (report: IReport) => {
    setDuplicateSourceReport(report);
    setDuplicateReportName(`${report.name} copy`);
    setIsDuplicateDialogOpen(true);
  };

  const closeDuplicateDialog = () => {
    setIsDuplicateDialogOpen(false);
    setDuplicateSourceReport(undefined);
    setDuplicateReportName("");
    setIsDuplicatingReport(false);
  };

  const duplicateReport = async () => {
    if (!duplicateSourceReport || !duplicateReportName.trim()) {
      return;
    }

    setIsDuplicatingReport(true);

    try {
      await api.post(`/reports/${duplicateSourceReport.report_id}/duplicate`, {
        customer_id: duplicateSourceReport.customer_id,
        name: duplicateReportName.trim(),
      });

      setGlobalMessage({
        action: PanelActionType.Success,
        owner: PanelActionOwner.Report,
        message: "Report duplicated successfully",
      });

      closeDuplicateDialog();
      getReports();
    } catch (error) {
      console.error(error);

      setGlobalMessage({
        action: PanelActionType.Error,
        owner: PanelActionOwner.Report,
        message: `Error duplicating report ${duplicateSourceReport.name}`,
      });

      setIsDuplicatingReport(false);
    }
  };

  const openDeleteDialog = (report: IReport) => {
    setDeleteSourceReport(report);
    setDeleteConfirmed(false);
    setIsDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeleteSourceReport(undefined);
    setDeleteConfirmed(false);
    setIsDeletingReport(false);
  };

  const deleteReport = async () => {
    if (!deleteSourceReport || !deleteConfirmed) {
      return;
    }

    setIsDeletingReport(true);

    try {
      await api.delete(`/reports/${deleteSourceReport.report_id}`);

      setGlobalMessage({
        action: PanelActionType.Success,
        owner: PanelActionOwner.Report,
        message: "Report marked as deleted",
      });

      closeDeleteDialog();
      getReports();
    } catch (error) {
      console.error(error);

      setGlobalMessage({
        action: PanelActionType.Error,
        owner: PanelActionOwner.Report,
        message: `Error deleting report ${deleteSourceReport.name}`,
      });

      setIsDeletingReport(false);
    }
  };

  return (
    <>
      {loading && (
        <div>
          <Spinner label="loading..." />
        </div>
      )}
      {!loading && (
        <>
          <PageTitle title="Submitted Reports" />
          <Breadcrumb
            items={getBreadcrumbItems()}
            maxDisplayedItems={10}
            ariaLabel="Breadcrumb with items rendered as buttons"
            overflowAriaLabel="More links"
          />
          <Separator />

          <Stack>
            {!id && (
              <>
                <Text>
                  To create a new report, go to the Customer Tab and navigate to
                  the site
                </Text>
                <Separator />
              </>
            )}

            <DataTable
              newButtonText="New Report"
              hideNewButton={id ? false : true}
              pageSize={pageSize}
              columns={[
                {
                  key: "name",
                  name: "Name",
                  fieldName: "name",
                  minWidth: 100,
                },
                {
                  key: "customer",
                  name: "Customer",
                  fieldName: "customer",
                  minWidth: 250,
                  onRender: (item) => <Text>{item.customer?.name}</Text>,
                },
                {
                  key: "site",
                  name: "Site",
                  fieldName: "site",
                  minWidth: 250,
                  onRender: (item) => <Text>{item.site?.name}</Text>,
                },

                {
                  key: "status",
                  name: "Status",
                  fieldName: "status",
                  minWidth: 100,
                  onRender: (item) => (
                    <Text
                      style={{
                        color: item.status === "Completed" ? "green" : "black",
                      }}
                    >
                      {item.status}
                    </Text>
                  ),
                },
                {
                  key: "created_at",
                  name: "Created Date",
                  fieldName: "created_at",
                  minWidth: 150,
                  onRender: (item) => (
                    <Text
                      style={{
                        color: item.status === "Completed" ? "green" : "black",
                      }}
                    >
                      {dayjs.unix(item.created_at).format("DD/MM/YYYY HH:mm")}
                    </Text>
                  ),
                },

                {
                  key: "actions",
                  name: "Actions",
                  minWidth: 140,
                  isResizable: false,
                  onRender: (item) => (
                    <Stack horizontal>
                      <TooltipHost
                        content="View Report"
                        // This id is used on the tooltip itself, not the host
                        // (so an element with this id only exists when the tooltip is shown)
                        id={detailTooltipId + "-view"}
                      >
                        <IconButton
                          aria-describedby={detailTooltipId + "-view"}
                          iconProps={{ iconName: "View" }}
                          onClick={() => {
                            navigate(`/report/${item.report_id}`);
                          }}
                        />
                      </TooltipHost>
                      <TooltipHost
                        content="Duplicate Report"
                        id={detailTooltipId + "-duplicate"}
                      >
                        <IconButton
                          disabled={item.status === "Draft"}
                          aria-describedby={detailTooltipId + "-duplicate"}
                          iconProps={{ iconName: "Copy" }}
                          onClick={() => {
                            openDuplicateDialog(item);
                          }}
                        />
                      </TooltipHost>
                      {item.status === "Submitted" ? (
                        <TooltipHost
                          content="Archive Report"
                          id={detailTooltipId + "-mark"}
                        >
                          <IconButton
                            aria-describedby={detailTooltipId + "-mark"}
                            iconProps={{ iconName: "Archive" }}
                            onClick={async () => {
                              try {
                                const response = await api.put(
                                  `/reports/${item.report_id}`,
                                  {
                                    status: "Completed",
                                  },
                                );
                                if (
                                  response &&
                                  response.status >= 200 &&
                                  response.status < 300
                                ) {
                                  // Update the status locally only if API call is successful
                                  setReports((prevReports) =>
                                    prevReports.map((r) =>
                                      r.report_id === item.report_id
                                        ? { ...r, status: "Completed" }
                                        : r,
                                    ),
                                  );
                                }
                              } catch (error) {
                                console.error(error);
                              }
                            }}
                          />
                        </TooltipHost>
                      ) : (
                        <TooltipHost
                          content="Unarchive Report"
                          id={detailTooltipId + "-mark"}
                        >
                          <IconButton
                            disabled={
                              item.status === "Draft" ||
                              tenantInfo?.roles.includes("customer_viewer")
                            }
                            aria-describedby={detailTooltipId + "-mark"}
                            iconProps={{ iconName: "Undo" }}
                            onClick={async () => {
                              try {
                                const response = await api.put(
                                  `/reports/${item.report_id}`,
                                  {
                                    status: "Submitted",
                                  },
                                );
                                if (
                                  response &&
                                  response.status >= 200 &&
                                  response.status < 300
                                ) {
                                  // Update the status locally only if API call is successful
                                  setReports((prevReports) =>
                                    prevReports.map((r) =>
                                      r.report_id === item.report_id
                                        ? { ...r, status: "Submitted" }
                                        : r,
                                    ),
                                  );
                                }
                              } catch (error) {
                                console.error(error);
                              }
                            }}
                          />
                        </TooltipHost>
                      )}
                      <TooltipHost
                        content="Delete Report"
                        id={detailTooltipId + "-delete"}
                      >
                        <IconButton
                          disabled={item.status !== "Draft"}
                          aria-describedby={detailTooltipId + "-delete"}
                          iconProps={{ iconName: "Delete" }}
                          styles={{
                            root: { color: "#a4262c" },
                            rootDisabled: { color: "#f4c6c8" },
                          }}
                          onClick={() => {
                            openDeleteDialog(item);
                          }}
                        />
                      </TooltipHost>
                    </Stack>
                  ),
                },
              ]}
              items={reports}
              totalItems={totalReports}
              onNewItemClick={() => {
                setCurrentReport(undefined);
                setIsEditReportOpen(true);
              }}
              onItemClicked={(item: IReport) =>
                navigate(`/report/${item.report_id}`)
              }
              currentPage={currentPage}
              onPaginationClick={(page: number) => {
                setCurrentPage(page);
              }}
              onSearchTextChange={(text: string) => {
                setCurrentPage(1);
                setSearchTerm(text);
              }}
              onPageSizeChange={(size: number) => {
                setPageSize(size);
              }}
            />

            <ReportEditPanel
              formMode={currentReport ? FormMode.Edit : FormMode.Add}
              isOpen={isEditReportOpen}
              site_id={site ? site.site_id : ""}
              dismissFunction={() => setIsEditReportOpen(false)}
              successFunction={() => {
                setCurrentReport(undefined);
                setIsEditReportOpen(false);
                getReports();
              }}
              customer_id={customer ? customer.customer_id : ""}
              report_id={currentReport ? currentReport.report_id : ""}
            />

            <Dialog
              hidden={!isDuplicateDialogOpen}
              onDismiss={closeDuplicateDialog}
              dialogContentProps={{
                type: DialogType.normal,
                title: "Duplicate Report",
                subText: duplicateSourceReport
                  ? `Create a copy of '${duplicateSourceReport.name}'.`
                  : "Create a copy of this report.",
              }}
            >
              <Text
                styles={{
                  root: {
                    marginBottom: 8,
                  },
                }}
              >
                {`Copying report: ${duplicateSourceReport?.name ?? ""}`}
              </Text>
              <TextField
                label="New report name"
                value={duplicateReportName}
                onChange={(_, value) => setDuplicateReportName(value ?? "")}
                required
              />
              <DialogFooter>
                <PrimaryButton
                  onClick={duplicateReport}
                  text={isDuplicatingReport ? "Duplicating..." : "Duplicate"}
                  disabled={isDuplicatingReport || !duplicateReportName.trim()}
                />
                <DefaultButton onClick={closeDuplicateDialog} text="Cancel" />
              </DialogFooter>
            </Dialog>

            <Dialog
              hidden={!isDeleteDialogOpen}
              onDismiss={closeDeleteDialog}
              dialogContentProps={{
                type: DialogType.normal,
                title: "Delete Report",
                subText:
                  "Are you sure you want to mark this report as deleted? This can only be undone by contacting Yorcloud",
              }}
            >
              <Checkbox
                label="I confirm I want to mark this report as deleted"
                checked={deleteConfirmed}
                onChange={(_, checked) => setDeleteConfirmed(!!checked)}
                styles={{ root: { marginTop: 8 } }}
              />
              <DialogFooter>
                <PrimaryButton
                  onClick={deleteReport}
                  text={isDeletingReport ? "Deleting..." : "Confirm"}
                  disabled={isDeletingReport || !deleteConfirmed}
                  styles={{
                    root: {
                      backgroundColor: "#a4262c",
                      borderColor: "#a4262c",
                    },
                    rootHovered: {
                      backgroundColor: "#8b1f24",
                      borderColor: "#8b1f24",
                    },
                    rootPressed: {
                      backgroundColor: "#751a1e",
                      borderColor: "#751a1e",
                    },
                    rootDisabled: {
                      backgroundColor: "#f4c6c8",
                      borderColor: "#f4c6c8",
                    },
                  }}
                />
                <DefaultButton onClick={closeDeleteDialog} text="Cancel" />
              </DialogFooter>
            </Dialog>
          </Stack>
        </>
      )}
    </>
  );
};

export default Reports;
